import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Footer from "../../structures/Footer";
import "../../../assets/css/booking-success.css";

export default function PendingPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const queryId = searchParams.get("bookingId") || searchParams.get("id");
  const API_BASE = process.env.REACT_APP_BACKEND_API || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');

  const [bookingData, setBookingData] = useState(() => {
    if (state) return state;
    const saved = localStorage.getItem("bookingData");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Jika ID di URL beda dengan yang di localStorage, jangan pakai data localStorage
        if (queryId && parsed.bookingId !== queryId) {
          return null;
        }
        return parsed;
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  
  const [isLoadingData, setIsLoadingData] = useState(!!queryId && !bookingData);

  const bookingId = bookingData?.bookingId || queryId || null;

  useEffect(() => {
    if (!bookingId) return;

    const fetchFullData = async () => {
      setIsLoadingData(true);
      try {
        const res = await fetch(`${API_BASE}/api/bookings/${bookingId}`, {
          headers: { "ngrok-skip-browser-warning": "true" }
        });
        const json = await res.json();
        if (json.success && json.data) {
          const freshData = Array.isArray(json.data) ? json.data[0] : json.data;
          
          // SECURITY CHECK: Jika ternyata sudah lunas atau gagal, lempar ke halaman yang sesuai
if (freshData.status === "PAID") {
             const isMantos = freshData?.experience?.id?.startsWith('mantos-');
             clearInterval(pollInterval);
             navigate(`${isMantos ? '/mantos/booking/success' : '/booking/success'}?bookingId=${bookingId}`, { state: freshData, replace: true });
             return;
           } else if (freshData.status === "FAILED") {
             const isMantos = freshData?.experience?.id?.startsWith('mantos-');
             clearInterval(pollInterval);
             navigate(`${isMantos ? '/mantos/booking/failed' : '/booking/failed'}?bookingId=${bookingId}`, { replace: true });
             return;
           }
          
          setBookingData(freshData);
        }
      } catch (err) {
        console.error("Failed to fetch fresh booking data", err);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchFullData();

    // 🔥 Polling: tanya DOKU langsung tiap 10 detik (active status check)
    const pollInterval = setInterval(async () => {
      try {
        // Gunakan endpoint check-status yang langsung tanya ke DOKU API
        const res = await fetch(`${API_BASE}/api/payment/check-status/${bookingId}`, {
          headers: { "ngrok-skip-browser-warning": "true" }
        });
        const json = await res.json();
        if (json.success) {
          const status = json.status || json.data?.status;
          console.log(`[Polling] ${bookingId} status: ${status}`);
          if (status === "PAID") {
            const isMantos = json.data?.experience?.id?.startsWith('mantos-');
            clearInterval(pollInterval);
            navigate(`${isMantos ? '/mantos/booking/success' : '/booking/success'}?bookingId=${bookingId}`, { state: json.data, replace: true });
          } else if (status === "FAILED") {
            const isMantos = json.data?.experience?.id?.startsWith('mantos-');
            clearInterval(pollInterval);
            navigate(`${isMantos ? '/mantos/booking/failed' : '/booking/failed'}?bookingId=${bookingId}`, { replace: true });
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 10000); // Cek tiap 10 detik (hindari spam ke DOKU)

    return () => clearInterval(pollInterval);
  }, [bookingId, API_BASE, navigate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const elements = document.querySelectorAll(".animate-on-scroll");
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("animate");
            }
          });
        },
        { threshold: 0.1 }
      );
      elements.forEach((el) => observer.observe(el));
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [isLoadingData, bookingData]);

  if (isLoadingData) {
    return (
      <div className="success-page-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Checking booking status...</p>
        </div>
      </div>
    );
  }

  if (!bookingData) {
    return (
      <div className="success-page-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h2>Booking Not Found</h2>
          <p>Silakan lakukan booking ulang atau periksa pembayaran Anda.</p>
          <button onClick={() => navigate("/reservation")}>Back to Experiences</button>
        </div>
      </div>
    );
  }

const { name, email, phone, pax, date, time, experience, totalPrice } = bookingData;

    const getExperienceDetails = (expVal) => {
      if (expVal && typeof expVal === 'object' && expVal.name) return expVal;
      return { name: bookingData?.experienceName || "Premium Experience", icon: "🎫", duration: "Various", price: totalPrice / (pax || 1) };
    };

    const expDetails = getExperienceDetails(experience);
    const isMantosExperience = experience?.id?.startsWith('mantos-');

  return (
    <div className="success-page-container">
      <section className="success-header animate-on-scroll">
        <div className="success-icon-wrapper">
          <div className="success-icon pending">!</div>
          <div className="success-ring"></div>
        </div>
        <h1 className="success-title">Payment Pending</h1>
        <p className="success-subtitle">
          Your booking is reserved, but we are waiting for your payment
        </p>
        <div className="success-ornament">◆ ◆ ◆</div>
      </section>

      <section className="success-content animate-on-scroll">
        <div className="success-content-wrapper">
          <div className="confirmation-card">
            <div className="card-header">
              <h2>Order Summary</h2>
              <div className="booking-id">
                <span className="id-label">Booking ID</span>
                <span className="id-value">{bookingId}</span>
              </div>
            </div>
            <div className="info-section">
              <h3 className="section-title">Guest Information</h3>
              <div className="info-grid">
                <div className="info-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '25px' }}>
                  <span className="info-icon" style={{ fontSize: '24px', minWidth: '30px', textAlign: 'center' }}>👤</span>
                  <div className="info-content" style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                    <span className="info-label" style={{ fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#999', fontWeight: '600' }}>Full Name</span>
                    <span className="info-value" style={{ fontSize: '16px', color: '#111', fontFamily: '"Playfair Display", serif' }}>{name}</span>
                  </div>
                </div>
                <div className="info-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '25px' }}>
                  <span className="info-icon" style={{ fontSize: '24px', minWidth: '30px', textAlign: 'center' }}>📧</span>
                  <div className="info-content" style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                    <span className="info-label" style={{ fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#999', fontWeight: '600' }}>Email</span>
                    <span className="info-value" style={{ fontSize: '16px', color: '#111', fontFamily: '"Playfair Display", serif' }}>{email}</span>
                  </div>
                </div>
                <div className="info-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '25px' }}>
                  <span className="info-icon" style={{ fontSize: '24px', minWidth: '30px', textAlign: 'center' }}>📅</span>
                  <div className="info-content" style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                    <span className="info-label" style={{ fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#999', fontWeight: '600' }}>Date</span>
                    <span className="info-value" style={{ fontSize: '16px', color: '#111', fontFamily: '"Playfair Display", serif' }}>{date}</span>
                  </div>
                </div>
                <div className="info-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '25px' }}>
                  <span className="info-icon" style={{ fontSize: '24px', minWidth: '30px', textAlign: 'center' }}>🕐</span>
                  <div className="info-content" style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                    <span className="info-label" style={{ fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#999', fontWeight: '600' }}>Time</span>
                    <span className="info-value" style={{ fontSize: '16px', color: '#111', fontFamily: '"Playfair Display", serif' }}>{time}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="info-section payment-section">
              <div className="payment-details">
                <div className="payment-row total"><span>Total Amount</span><span>IDR {totalPrice.toLocaleString()}</span></div>
              </div>
              <div className="payment-status">
                <span className="status-badge pending"><span className="status-dot"></span>Awaiting Payment</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="success-actions animate-on-scroll">
<button 
           className="action-btn" 
           style={{ 
             background: '#5A5A5A', 
             borderColor: '#5A5A5A', 
             color: '#fff',
             display: 'flex',
             alignItems: 'center',
             gap: '12px',
             padding: '18px 35px',
             fontFamily: '"Outfit", sans-serif',
             fontSize: '13px',
             fontWeight: '600',
             letterSpacing: '2px',
             textTransform: 'uppercase',
             borderRadius: '2px',
             cursor: 'pointer'
           }}
           onClick={() => navigate(isMantosExperience ? "/mantos/booking/payment" : "/booking/payment")}
         >
           <span>Complete Payment</span><span className="btn-icon">💳</span>
         </button>
        <button className="action-btn secondary" onClick={() => navigate("/")}>
          <span>Back to Home</span><span className="btn-icon">🏠</span>
        </button>
      </section>
      <Footer />
    </div>
  );
}
